import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CalendarHeart, Check, ChevronLeft, Search } from "lucide-react";
import { calFetch } from "@/lib/calApi";
import { useCalAuth } from "@/lib/calAuth";

function PersonPicker({ people, value, onChange, mode }) {
  const [q, setQ] = useState("");
  const groups = useMemo(() => {
    const norm = (s) => s.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
    const list = people.filter((p) => norm(`${p.name} ${p.nickname}`).includes(norm(q)));
    return list.reduce((acc, p) => { (acc[p.branch || "Familia"] = acc[p.branch || "Familia"] || []).push(p); return acc; }, {});
  }, [people, q]);
  return (
    <div>
      <div className="relative mb-2">
        <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-cocoa-soft" />
        <input data-testid="cal-person-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Busca tu nombre…"
          className="w-full pl-10 pr-4 py-3 rounded-2xl bg-paper border border-line text-cocoa focus:outline-none focus:ring-2 focus:ring-amber-deep/30" />
      </div>
      <div className="max-h-72 overflow-y-auto rounded-2xl border border-line bg-paper/60 no-scrollbar" data-testid="cal-person-list">
        {Object.entries(groups).map(([branch, list]) => (
          <div key={branch}>
            <p className="sticky top-0 bg-[#f6ead0]/95 backdrop-blur px-4 py-1.5 text-[10px] uppercase tracking-[0.2em] text-amber-deep font-semibold">{branch}</p>
            {list.map((p) => {
              const disabled = mode === "register" ? p.registered : !p.registered;
              return (
                <button type="button" key={p.id} data-testid={`cal-person-${p.id}`} onClick={() => onChange(p.id)} disabled={disabled}
                  className={`w-full flex items-center justify-between px-4 py-2.5 text-left text-sm transition-colors ${value === p.id ? "bg-night text-white" : "text-cocoa hover:bg-cream"} disabled:opacity-35 disabled:cursor-not-allowed`}>
                  <span>{p.name}{p.nickname ? <span className="opacity-60"> · {p.nickname}</span> : null}</span>
                  {value === p.id ? <Check className="w-4 h-4 text-candle" /> : disabled && <span className="text-[10px] italic">{mode === "register" ? "ya tiene clave" : "sin clave aún"}</span>}
                </button>
              );
            })}
          </div>
        ))}
        {!Object.keys(groups).length && <p className="px-4 py-6 text-sm text-cocoa-soft italic">No encontramos ese nombre. Pídele al Pollo que te agregue.</p>}
      </div>
    </div>
  );
}

export default function CalEntrar() {
  const { enter } = useCalAuth();
  const nav = useNavigate();
  const [people, setPeople] = useState([]);
  const [mode, setMode] = useState("login");
  const [pid, setPid] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    calFetch("/cal/people-public").then((r) => setPeople(r.people)).catch((e) => toast.error(e.message));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!pid) return toast.error("Elige tu nombre de la lista");
    if (mode === "register" && pw !== pw2) return toast.error("Las claves no coinciden");
    setBusy(true);
    try {
      const p = await enter(mode, pid, pw);
      toast.success(`¡Hola, ${p.nickname || p.name.split(" ")[0]}!`);
      nav("/calendario", { replace: true });
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  const switchMode = (m) => { setMode(m); setPid(""); setPw(""); setPw2(""); };

  return (
    <div className="min-h-screen paper-texture iphone-safe-top px-5 py-8">
      <div className="max-w-md mx-auto">
        <Link to="/" data-testid="cal-entrar-back" className="inline-flex items-center gap-1 text-sm text-cocoa-soft hover:text-cocoa mb-6"><ChevronLeft className="w-4 h-4" /> Inicio</Link>
        <div className="cal-rise mb-7">
          <div className="w-12 h-12 rounded-2xl bg-night flex items-center justify-center mb-5"><CalendarHeart className="w-6 h-6 text-candle" /></div>
          <h1 className="font-display italic text-cocoa text-4xl sm:text-5xl leading-tight">Calendario Familiar</h1>
          <p className="font-serif text-cocoa-soft text-base md:text-lg mt-2">Cada uno con su nombre y su propia clave, pa' que te lleguen tus avisos.</p>
        </div>
        <div className="cal-rise grid grid-cols-2 p-1 rounded-full bg-cream border border-line mb-5" style={{ animationDelay: "100ms" }}>
          {[["login", "Ya tengo clave"], ["register", "Primera vez"]].map(([m, l]) => (
            <button key={m} type="button" data-testid={`cal-mode-${m}`} onClick={() => switchMode(m)}
              className={`rounded-full py-2.5 text-sm font-semibold transition-colors ${mode === m ? "bg-night text-white" : "text-cocoa-soft"}`}>{l}</button>
          ))}
        </div>
        <form onSubmit={submit} className="cal-rise space-y-4" style={{ animationDelay: "180ms" }}>
          <PersonPicker people={people} value={pid} onChange={setPid} mode={mode} />
          <input data-testid="cal-password-input" type="password" value={pw} onChange={(e) => setPw(e.target.value)}
            placeholder={mode === "register" ? "Crea tu clave (mínimo 4)" : "Tu clave"} autoComplete={mode === "register" ? "new-password" : "current-password"}
            className="w-full px-4 py-3 rounded-2xl bg-paper border border-line text-cocoa focus:outline-none focus:ring-2 focus:ring-amber-deep/30" />
          {mode === "register" && (
            <input data-testid="cal-password-confirm" type="password" value={pw2} onChange={(e) => setPw2(e.target.value)} placeholder="Repite tu clave" autoComplete="new-password"
              className="w-full px-4 py-3 rounded-2xl bg-paper border border-line text-cocoa focus:outline-none focus:ring-2 focus:ring-amber-deep/30" />
          )}
          <button data-testid="cal-submit-btn" disabled={busy} className="w-full rounded-full bg-night text-white py-3.5 font-semibold cal-press disabled:opacity-50">
            {busy ? "Entrando…" : mode === "register" ? "Crear mi clave y entrar" : "Entrar"}
          </button>
          <p className="text-xs text-center text-cocoa-soft">¿Se te olvidó la clave? Pídele al Pollo que te la resetee.</p>
        </form>
      </div>
    </div>
  );
}
